// 📄 pages/Proyectos/BuscarProveedores.tsx
//
// "Buscar Proveedores" (SLP, 2026-09-05, a pedido del usuario) — pantalla
// dedicada para el endpoint más usado de la API "Supplier Data
// Pagination" de Ariba (obtener proveedores, con sus filtros). Reusa las
// credenciales de "Configuración > APIs" (tipo Ariba, OAuth2) — mismo
// store que ApiDefinition, ver clienteConexionService.ts — en vez de
// crear un sistema de credenciales aparte. Si el proyecto todavía no
// tiene ninguna API Ariba configurada ahí, se muestra un aviso con link
// directo a esa pantalla (mismo criterio que AribaApiTester.tsx).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, RefreshCw, AlertTriangle, KeyRound, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';
import { obtenerConfigCliente, ApisCredenciales, ApiDefinition } from '../../services/clienteConexionService.ts';
import {
  buscarProveedores,
  FiltrosProveedores,
  FILTROS_PROVEEDORES_VACIOS,
  Proveedor
} from '../../services/slpProveedoresService.ts';
import PillMultiSelect from '../../components/ui/PillMultiSelect.tsx';
import LimiteUsoAlerta from '../../components/ui/LimiteUsoAlerta.tsx';
import ProveedorCard from './components/ProveedorCard.tsx';
import ProveedorDetallePanel from './components/ProveedorDetallePanel.tsx';

// Valores comunes vistos en respuestas reales de Ariba SLP — se muestran
// como píldoras de un click en PillMultiSelect, sin impedir que el
// usuario escriba cualquier otro valor a mano (el enum completo de Ariba
// no está documentado públicamente, así que esto es "lo más frecuente",
// no una lista cerrada).
const SUGERENCIAS_CALIFICACION = ['Qualified', 'QualificationStarted', 'InQualification', 'QualifiedForSome', 'Expired', 'PendingQualificationApproval'];
const SUGERENCIAS_REGISTRO = ['Registered', 'InRegistration'];
const SUGERENCIAS_REGION = ['USA', 'INDIA', 'GTM', 'DEU', 'EMEA', 'APAC', 'NAMER'];
const SUGERENCIAS_NIVEL = ['0', '1', '2', '3', '4', '5'];

const TOGGLES_RESPUESTA: { campo: keyof FiltrosProveedores; label: string }[] = [
  { campo: 'withQuestionnaire', label: 'Cuestionarios' },
  { campo: 'withGenericCustomFields', label: 'Campos personalizados' },
  { campo: 'withBankDetail', label: 'Datos bancarios' },
  { campo: 'withTaxDetail', label: 'Datos fiscales' },
  { campo: 'withCompanyCodeDetail', label: 'Company Codes' },
  { campo: 'withDisqualifications', label: 'Descalificaciones' }
];

export default function BuscarProveedores() {
  const { projectId, project } = useProject();
  const clienteId = project?.clienteId || '';
  const { profile } = useAuth();
  // Cuota mensual de "API Tester", compartida con AribaApiTester.tsx y
  // BuscarFacturas.tsx (ver functions/services/monthlyQuota.js) — reset
  // perezoso, así que un contador de un mes viejo se muestra como 0.
  const mesActual = `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`;
  const usoApiTesterVigente = profile?.usoApiTesterMes === mesActual ? (profile?.usoApiTesterCantidad ?? 0) : 0;

  const [apisAriba, setApisAriba] = useState<ApiDefinition[]>([]);
  const [cargandoApis, setCargandoApis] = useState(true);
  const [apiIdSeleccionada, setApiIdSeleccionada] = useState('');

  const [filtros, setFiltros] = useState<FiltrosProveedores>(FILTROS_PROVEEDORES_VACIOS);
  const [avanzadosAbiertos, setAvanzadosAbiertos] = useState(false);

  const [buscando, setBuscando] = useState(false);
  const [yaBusco, setYaBusco] = useState(false);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [total, setTotal] = useState(0);
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null);
  const [proveedorAbierto, setProveedorAbierto] = useState<Proveedor | null>(null);

  useEffect(() => {
    if (!clienteId) { setCargandoApis(false); return; }
    let cancelado = false;
    setCargandoApis(true);
    obtenerConfigCliente<ApisCredenciales>(clienteId, 'apis')
      .then(({ apis }) => {
        if (cancelado) return;
        // Filtra por el path esperado, no solo por tipo — un proyecto puede
        // tener varias APIs Ariba configuradas para propósitos distintos
        // (esta misma + Invoice Extraction, Flow Extension, ...) y mostrar
        // todas en el selector obligaría a elegir a mano incluso cuando
        // hay una sola que realmente sirve para esta búsqueda.
        const ariba = (apis || []).filter((a) => a.tipo === 'ariba' && (a.api || '').toLowerCase().includes('supplierdatapagination'));
        setApisAriba(ariba);
        if (ariba.length === 1) setApiIdSeleccionada(ariba[0].id);
      })
      .catch((err) => console.error('Error cargando las APIs Ariba configuradas:', err))
      .finally(() => { if (!cancelado) setCargandoApis(false); });
    return () => { cancelado = true; };
  }, [clienteId]);

  const actualizarFiltro = <K extends keyof FiltrosProveedores>(campo: K, valor: FiltrosProveedores[K]) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const cantidadFiltrosAvanzados = useMemo(() => {
    return [filtros.businessUnitList, filtros.categoryList, filtros.preferredLevelList, filtros.qualificationStatusList, filtros.regionList, filtros.registrationStatusList]
      .reduce((acc, lista) => acc + lista.length, 0);
  }, [filtros]);

  const handleBuscar = async () => {
    if (!apiIdSeleccionada || !projectId) return;
    setBuscando(true);
    setYaBusco(true);
    setErrorBusqueda(null);
    try {
      const resultado = await buscarProveedores(clienteId, projectId, apiIdSeleccionada, filtros);
      if (!resultado.ok) {
        setErrorBusqueda(resultado.error || 'No se pudo completar la búsqueda.');
        setProveedores([]);
        setTotal(0);
      } else {
        setProveedores(resultado.proveedores);
        setTotal(resultado.count);
      }
    } catch (err: any) {
      setErrorBusqueda(err.message || 'No se pudo completar la búsqueda.');
      setProveedores([]);
      setTotal(0);
    } finally {
      setBuscando(false);
    }
  };

  if (cargandoApis) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
        <p className="text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-light text-white flex items-center gap-2.5">
          <Building2 className="w-6 h-6 text-indigo-400" /> Buscar Proveedores
        </h1>
        <p className="text-sm text-gray-400 mt-1">Consulta proveedores de SLP directo desde Ariba (Supplier Data Pagination) — registro, calificaciones, cuestionarios y más.</p>
      </div>

      {apisAriba.length === 0 ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Sin API de Ariba configurada</p>
            <p className="text-xs text-amber-200/80 mt-1">
              Esta búsqueda necesita una API tipo Ariba (OAuth2) en Configuración &gt; APIs, con el campo "API" en <code className="font-mono">/api/supplierdatapagination/v4/prod</code>.
            </p>
            {projectId && (
              <Link to={`/proyecto/${projectId}/config`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 mt-2 transition-colors">
                Ir a Configuración &gt; APIs
              </Link>
            )}
          </div>
        </div>
      ) : (
        <>
          {apisAriba.length > 1 && (
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">API de Ariba a usar</label>
              <select
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                value={apiIdSeleccionada}
                onChange={(e) => setApiIdSeleccionada(e.target.value)}
              >
                <option value="">Elegí una API...</option>
                {apisAriba.map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre || 'Sin nombre'}</option>
                ))}
              </select>
            </div>
          )}

          <LimiteUsoAlerta
            cantidad={usoApiTesterVigente}
            limite={profile?.limiteApiTesterMensual}
            etiqueta="ejecuciones de API Tester este mes"
          />

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PillMultiSelect
                label="SM Vendor ID"
                valores={filtros.smVendorIds}
                onChange={(v) => actualizarFiltro('smVendorIds', v)}
                placeholder="Ej: S123456 — Enter para agregar"
              />
              <PillMultiSelect
                label="ERP Vendor ID"
                valores={filtros.erpVendorIds}
                onChange={(v) => actualizarFiltro('erpVendorIds', v)}
                placeholder="Ej: 9877234512 — Enter para agregar"
              />
            </div>

            <button
              type="button"
              onClick={() => setAvanzadosAbiertos((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filtros avanzados{cantidadFiltrosAvanzados > 0 ? ` (${cantidadFiltrosAvanzados})` : ''}
              {avanzadosAbiertos ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {avanzadosAbiertos && (
              <div className="space-y-4 pt-2 border-t border-white/10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PillMultiSelect
                    label="Unidad de negocio"
                    valores={filtros.businessUnitList}
                    onChange={(v) => actualizarFiltro('businessUnitList', v)}
                    placeholder="Ej: 408"
                  />
                  <PillMultiSelect
                    label="Categoría (UNSPSC)"
                    valores={filtros.categoryList}
                    onChange={(v) => actualizarFiltro('categoryList', v)}
                    placeholder="Ej: 51101500"
                  />
                  <PillMultiSelect
                    label="Nivel preferido"
                    valores={filtros.preferredLevelList}
                    onChange={(v) => actualizarFiltro('preferredLevelList', v)}
                    sugerencias={SUGERENCIAS_NIVEL}
                  />
                  <PillMultiSelect
                    label="Región"
                    valores={filtros.regionList}
                    onChange={(v) => actualizarFiltro('regionList', v)}
                    sugerencias={SUGERENCIAS_REGION}
                    placeholder="Otra región..."
                  />
                  <PillMultiSelect
                    label="Estado de calificación"
                    valores={filtros.qualificationStatusList}
                    onChange={(v) => actualizarFiltro('qualificationStatusList', v)}
                    sugerencias={SUGERENCIAS_CALIFICACION}
                  />
                  <PillMultiSelect
                    label="Estado de registro"
                    valores={filtros.registrationStatusList}
                    onChange={(v) => actualizarFiltro('registrationStatusList', v)}
                    sugerencias={SUGERENCIAS_REGISTRO}
                  />
                </div>

                <div className="pt-2 border-t border-white/10">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Incluir en la respuesta</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {TOGGLES_RESPUESTA.map(({ campo, label }) => (
                      <label key={campo} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filtros[campo] as boolean}
                          onChange={(e) => actualizarFiltro(campo, e.target.checked as any)}
                          className="accent-indigo-500"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleBuscar}
              disabled={buscando || !apiIdSeleccionada}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
            >
              {buscando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {buscando ? 'Buscando...' : 'Buscar proveedores'}
            </button>
          </div>

          {errorBusqueda && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {errorBusqueda}
            </div>
          )}

          {yaBusco && !errorBusqueda && !buscando && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">{total} proveedor{total !== 1 ? 'es' : ''} encontrado{total !== 1 ? 's' : ''}</p>
              {proveedores.length === 0 ? (
                <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  Ningún proveedor coincide con estos filtros.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {proveedores.map((p, i) => (
                    <ProveedorCard key={p['SM Vendor ID'] || i} proveedor={p} onVerDetalle={() => setProveedorAbierto(p)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {proveedorAbierto && (
        <ProveedorDetallePanel proveedor={proveedorAbierto} onClose={() => setProveedorAbierto(null)} />
      )}
    </div>
  );
}
